import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useLocationGroupStore } from '@frontend/scope';
import { executeWetagBulkQuery, wetagQueryKeys, getWetagConfig } from './api';
import { normalizeToEntityQuery, normalizeToQuery } from './query-builder';
import {
  WetagQueryOptions,
  WetagInput,
  WetagBulkQueryAggregationMode,
  WetagBulkResult,
  WetagBulkQueryResponse,
} from './types';

const DEFAULT_OPTIONS: Required<WetagQueryOptions> = {
  cacheTime: 10 * 60 * 1000, // 10 minutes
  staleTime: 10 * 60 * 1000, // 10 minutes
  defaultValue: false,
  enabled: true,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

export function useWetagBulk<TTag extends string = string>(
  namespaceId: string,
  tag: TTag | TTag[] | false,
  inputs: WetagInput<TTag>[] | string[],
  strategy?: WetagBulkQueryAggregationMode,
  options: WetagQueryOptions = {}
): [boolean, boolean, WetagBulkQueryResponse | undefined] {
  const result = useWetagBulkQuery(namespaceId, tag, inputs, strategy, options);
  return [result.isEnabled, result.isLoading, result.response];
}

export function useWetagBulkQuery<TTag extends string = string>(
  namespaceId: string,
  tag: TTag | TTag[] | false,
  inputs: WetagInput<TTag>[] | string[],
  strategy: WetagBulkQueryAggregationMode = 'ANY',
  options: WetagQueryOptions = {}
): WetagBulkResult {
  const config = getWetagConfig();

  const hasLocationsSelected = useLocationGroupStore((state) => state.selectedLocationIds.length > 0);

  const tags = useMemo(() => (tag === false || !tag.length ? false : Array.isArray(tag) ? tag : [tag]), [tag]);

  const queries = useMemo(() => {
    return inputs
      .map((input) => {
        if (tags) {
          return tags.map((tag) => normalizeToEntityQuery(tag, input));
        }
        return [normalizeToQuery(input)];
      })
      .flat();
  }, [tags, inputs]);

  const mergedOptions = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...config.defaultOptions,
      ...options,
    }),
    [config.defaultOptions, options]
  );

  const { cacheTime, staleTime, defaultValue, enabled, refetchOnWindowFocus, refetchOnMount, refetchOnReconnect } =
    mergedOptions;

  const isQueryEnabled = enabled && hasLocationsSelected && inputs.filter(Boolean).length > 0;

  /**
   * TODO: Does not have sherlock override support yet
   */

  const reactQuery = useQuery(
    wetagQueryKeys.queryBulk(namespaceId, queries),
    () => executeWetagBulkQuery({ namespaceId, queries, strategy }),
    {
      enabled: isQueryEnabled,
      cacheTime,
      staleTime,
      retry: false,
      refetchOnWindowFocus,
      refetchOnMount,
      refetchOnReconnect,
    }
  );
  return {
    isEnabled: reactQuery.isError ? defaultValue : reactQuery.data?.matched ?? defaultValue,
    isLoading: reactQuery.isLoading,
    isError: reactQuery.isError,
    error: reactQuery.error as Error | null,
    isSuccess: reactQuery.isSuccess,
    refetch: reactQuery.refetch,
    response: reactQuery.data,
  };
}
